/**
 * @prettier
 */
/// <reference types="knockout">

var AbstractViewModel = require("./AbstractViewModel");
var NotificationSettingsViewModel = require("./NotificationsSettingsViewModel");

/**
 *
 * @param {UserPreferences} userPreferences
 * @param {MessagesWS} messagesWS
 * @param {StationUpdateWS} stationUpdateWS
 */
function SettingsViewModel(
  userPreferences,
  appViewModel,
  messagesWS,
  stationUpdateWS
) {
  var self = this;

  /**
   * Indicates whether the Settings UI should be shown
   *
   * @type {KnockoutObservable<boolean>}
   */
  this.isVisible = ko.observable(false);

  this.userPreferences = userPreferences;
  this.appViewModel = appViewModel;
  this.messagesWS = messagesWS;
  this.stationUpdateWS = stationUpdateWS;
  this.notificationSettingsView = new NotificationSettingsViewModel(
    this.userPreferences,
    this.appViewModel.queues,
    this.appViewModel.selectedLocations,
    this.appViewModel.selectedQueues
  );

  // Settings
  this.preferences = {
    notificationSound: ko.observable(null),
    showDetailOnArrival: ko.observable(null),
    ticketClassifierId: ko.observable(null),
    language: ko.observable(null),
  };

  /**
   * Hide
   *
   * @param {SettingsViewModel} data
   * @param {Event} event
   */
  this.handleHide = function (data, event) {
    this.isVisible(false);
  };

  /**
   * Show
   *
   * @param {SettingsViewModel} data
   * @param {Event} event
   */
  this.handleShow = function (data, event) {
    this.isVisible(true);
    this.initializeValues();
    this.initializeDoorbellPreview();
  };

  /**
   * Save
   *
   * @param {SettingsViewModel} data
   * @param {Event} event
   */
  this.handleSave = function (data, event) {
    this.isVisible(false);

    this.notificationSettingsView.save();
    this.updateSettings();

    setTimeout(this.appViewModel.matchQueueTitleHeight, 300);
  };

  this.handleCancel = function (data, event) {
    this.isVisible(false);
    setTimeout(this.appViewModel.matchQueueTitleHeight, 300);
  };

  /**
   * @type {KnockoutComputed}
   */
  this.availableLanguages = ko.deferredComputed(function () {
    var languages = this.appViewModel.availableLanguages();
    return languages;
  }, this);

  /**
   * @method availableLanguagesLabel
   */
  this.availableLanguagesLabel = this._availableLanguagesLabel.bind(this);

  /**
   * Only allow the User to change the Language if there are multiple
   * languages to choose from.
   *
   * @return {boolean}
   */
  this.canChooseLanguage = function () {
    var result = this.availableLanguages().length > 1;
    return result;
  };

  /**
   * @type {KnockoutComputed<Array>}
   */
  this.ticketClassifiers = ko.deferredComputed(function () {
    var tcs = this.appViewModel.ticketClassifiers();
    return tcs;
  }, this);

  /**
   * Indicates if the device supports Notifications
   *
   * @type {KnockoutObservable<boolean>}
   */
  this.isDesktopNotificationsAvailable = ko.computed(function () {
    return "Notification" in window;
  });

  /**
   * Audio element used to play the Doorbell/Notification sound
   */
  this.doorbellPreview = new Audio();

  /**
   * A list of Sounds available to play as the Doorbell/Notification sound.
   * @returns {NotificationSounds}
   */
  this.notificationSounds = function () {
    return this.appViewModel.notificationSounds
      ? this.appViewModel.notificationSounds()
      : [];
  };

  /**
   * Play the selected doorbell/notification sound
   */
  this.handlePreview = function (data, event) {
    if (this.isNotificationSoundPlayable()) {
      this.doorbellPreview.setAttribute(
        "src",
        this.preferences.notificationSound().path
      );
      this.playDoorbellPreview();
    }
  };

  /**
   * Determines if the instance has any queues that use audio notifications
   *
   * @method usesAudio
   * @returns {Boolean}
   */
  this.canUseAudibleAlerts = ko.computed(function () {
    var queues =
      this.appViewModel && this.appViewModel.queues
        ? this.appViewModel.queues()
        : [];

    var audibleQueues = queues.filter(function (q) {
      return q.audibleAlertsOnEnter || q.audibleAlertsOnCheckIn;
    });

    var usesAudio = audibleQueues.length > 1;
    // TODO: This means the Employee won't be able to change the sound unless there are 2 more queues using audible alerts.
    // Is that correct?

    return usesAudio;
  }, this);
}

SettingsViewModel.prototype.isNotificationSoundPlayable = function () {
  var sound = this.preferences.notificationSound();
  return sound && sound.path;
};

SettingsViewModel.prototype.supportsMetadata = function () {
  // TODO: this is weird logic?
  return (
    this.appViewModel.supportsStationMetadata() ||
    this.appViewModel.supportsStationMetadata() === false
  );
};

/**
 * Copies the values from the `UserPreferences`.
 *
 * We don't want to apply changes to the preferences immediately, so
 * they are copied and then applied later.
 */
SettingsViewModel.prototype.initializeValues = function () {
  var keys = Object.keys(this.preferences);

  this.userPreferences.load();
  var values = this.userPreferences.preferences();

  keys.forEach(
    function (key) {
      this.preferences[key](values[key]);
    }.bind(this)
  );

  this.preferences.language(this.appViewModel.userLang());

  this.notificationSettingsView.load();
};

SettingsViewModel.prototype.initializeDoorbellPreview = function () {
  // Setup doorbell preview
  var sounds = this.notificationSounds();
  if (sounds && sounds.length > 0) {
    var notificationSound = this.preferences.notificationSound()
      ? this.preferences.notificationSound()
      : this.notificationSounds()[0];
    this.doorbellPreview = new Audio(notificationSound.path);
  }
};

/**
 * Applies and persists the User's settings
 */
SettingsViewModel.prototype.updateSettings = function () {
  if (this.supportsMetadata()) {
    this.applyStationMetadata();
  }

  this.updatePreferences();
  this.applyNotificationSound();
  this.applyLanguage();
};

SettingsViewModel.prototype.updatePreferences = function () {
  var keys = Object.keys(this.preferences);
  this.userPreferences.update(
    keys.reduce(
      function (preferences, key) {
        preferences[key] = this.preferences[key]();
        return preferences;
      }.bind(this),
      {}
    )
  );

  this.userPreferences.store();
};

/**
 * Play the selected doorbell/notificatioun sound.
 */
SettingsViewModel.prototype.playDoorbellPreview = function () {
  this.doorbellPreview.pause();
  this.doorbellPreview.currentTime = 0;
  this.doorbellPreview.play();
};

/**
 * Set the Path/URl to the doorbell sound in the main app.
 */
SettingsViewModel.prototype.applyNotificationSound = function () {
  var notificationSound = this.preferences.notificationSound();
  if (this.canUseAudibleAlerts() && notificationSound !== null) {
    this.appViewModel.doorbell.setAttribute(
      "src",
      this.preferences.notificationSound().path
    );
  }
};

/**
 * Set the Ticket Classifier for the Employee/Ticketer Station
 */
SettingsViewModel.prototype.applyStationMetadata = function () {
  this.stationUpdateWS.clearParams();
  this.stationUpdateWS.addParam(
    "ticketClassifierId",
    this.preferences.ticketClassifierId()
  );
  this.stationUpdateWS.doRequest(
    function () {
      this.userPreferences.set(
        "ticketClassifierId",
        this.preferences.ticketClassifierId()
      );
    }.bind(this)
  );
};

/**
 * Updates the Language in the Application
 * TODO: This should probably be in ViewModel, and run via callback
 */
SettingsViewModel.prototype.applyLanguage = function () {
  if (this.canChooseLanguage()) {
    this.appViewModel.setUserLanguage(this.preferences.language());

    // TODO: This isn't really a SettingsViewModel concern but it
    // remains here because it's more accessible under test here
    // until we get ViewModel testable
    this.messagesWS.doRequest();
    this.appViewModel.selectedLocations().forEach(function (location) {
      location.kioskMessagesWS.doRequest(null, true);
    });
  }
};

/**
 * Get the message/translation for the language/locale option.
 *
 * @param {string} locale
 * @returns
 */
SettingsViewModel.prototype._availableLanguagesLabel = function (locale) {
  if (locale && typeof locale === "string") {
    var parts = locale.split("_");
    return this.messagesWS.get("language." + parts[0], false, locale);
  }
  return "";
};

module.exports = SettingsViewModel;
